import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Alert} from '@forecast-it/design-system';
import {Button} from '@/web-components';
import {APPROVAL_STATUS} from '../../../constants';

type Props = {
	selectedTab: string;
	isNewStatuses: boolean;
	onRemindNotSubmitted: () => void;
	onRemindPending: () => void;
	onRemindDeclined: () => void;
};

const Strip = styled.div`
	position: sticky;
	left: 0;
	box-sizing: border-box;
	width: 100%;
	padding: 16px;
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 16px;
`;

// The banner takes the free width and wraps its text, so the buttons never have to shrink.
const BannerSlot = styled.div`
	flex: 1 1 auto;
	min-width: 0;
`;

const ButtonSlot = styled.div`
	flex-shrink: 0;
`;

/**
 * The strip above the time approval table: REMIND ALL on the tabs that have it and, on the
 * Not Submitted tab once the new approval statuses are on, an info banner explaining why
 * that tab still appears. With the new statuses, newly logged time starts as Pending, so only
 * time logged before the switch can still be Not Submitted.
 */
const TimeApprovalActionStrip = ({
	selectedTab,
	isNewStatuses,
	onRemindNotSubmitted,
	onRemindPending,
	onRemindDeclined,
}: Props) => {
	const intl = useIntl();

	const remindAllButton = (onClick: () => void) => (
		<ButtonSlot>
			<Button variant={Button.VARIANT.VERY_LIGHT_GRAY_OUTLINE} onClick={onClick}>
				{intl.formatMessage({id: 'timesheet.reminder_all_modal_header'})}
			</Button>
		</ButtonSlot>
	);

	return (
		<Strip>
			{selectedTab === APPROVAL_STATUS.NOT_SUBMITTED && (
				<>
					{isNewStatuses && (
						<BannerSlot data-cy={'time-approval-legacy-not-submitted-info'}>
							<Alert
								type={Alert.TYPE.INFORMATION}
								text={intl.formatMessage({id: 'time_approval.not_submitted_legacy_info'})}
							/>
						</BannerSlot>
					)}
					{remindAllButton(onRemindNotSubmitted)}
				</>
			)}
			{isNewStatuses && selectedTab === APPROVAL_STATUS.PENDING && remindAllButton(onRemindPending)}
			{isNewStatuses && selectedTab === APPROVAL_STATUS.DECLINED && remindAllButton(onRemindDeclined)}
		</Strip>
	);
};

export default TimeApprovalActionStrip;
